import Header from '@/components/Header';
import Footer from '@/components/ui/footer';
import HeroBass from '@/components/landing/bass/HeroBass';
import WhyChooseCelloBass from '@/components/landing/bass/WhyChooseCelloBass';
import AwardsBass from '@/components/landing/bass/AwardsBass';
import HowItWorksBass from '@/components/landing/bass/HowItWorksBass';
import JoySectionBass from '@/components/landing/bass/JoySectionBass';
import PricingEdits from '@/components/landing/bass/PricingEdits';
import FAQSectionBass from '@/components/landing/bass/FAQSectionBass';
import TestimonialsBass from '@/components/landing/bass/TestimonialsBass';
import LifestyleBandBass from '@/components/landing/bass/LifestyleBandBass';
import GapFinderQuiz from '@/components/landing/bass/GapFinderQuiz';
import CTABass from '@/components/landing/bass/CTABass';
import { motion, useScroll, useSpring } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import WaitlistDialog from "./../components/landing/bass/WaitlistDialog";

declare global {
    interface Window {
        Trustpilot?: { loadFromElement: (el: HTMLElement, reload?: boolean) => void };
    }
}

interface Landing8Props {
    /** 'queued' = post-waitlist-signup view (confirmation banner) at /queued.
     *  'mens'   = men's styling page at /mens-styling (portfolio filtered to men,
     *             How-It-Works step visuals swapped for photos). */
    variant?: 'default' | 'queued' | 'mens';
}

// Men's-page lifestyle photos shown in the "They're going to notice" section on /mens-styling.
const MENS_PHOTOS = [
    '/images/mens/mens-1.jpg',
    '/images/mens/mens-2.jpg',
    '/images/mens/mens-3.jpg',
];

const Landing8 = ({ variant = 'default' }: Landing8Props = {}) => {
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });
    const trustpilotRef = useRef<HTMLDivElement>(null);
    const [showWaitlist, setShowWaitlist] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const stickyRef = useRef<HTMLDivElement>(null);

    // Mobile: the progress bar + banner + header are one sticky group. Publish
    // its height as --sticky-h so in-page anchors (#features, #faq…) land below
    // it instead of underneath it. The banner wraps to 2–3 lines, so measure.
    useEffect(() => {
        const el = stickyRef.current;
        const root = rootRef.current;
        if (!el || !root) return;
        const ro = new ResizeObserver(([entry]) =>
            root.style.setProperty('--sticky-h', `${entry.contentRect.height}px`));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        // Add Playfair Display font
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);

        // Google tag (gtag.js)
        const gtagScript = document.createElement('script');
        gtagScript.async = true;
        gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=AW-17584005510';
        document.head.appendChild(gtagScript);

        const gtagInlineScript = document.createElement('script');
        gtagInlineScript.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'AW-17584005510');
        `;
        document.head.appendChild(gtagInlineScript);

        // Load Trustpilot widget
        if (window.Trustpilot && trustpilotRef.current) {
            window.Trustpilot.loadFromElement(trustpilotRef.current, true);
        }

        return () => {
            document.head.removeChild(link);
            document.head.removeChild(gtagScript);
            document.head.removeChild(gtagInlineScript);
        };
    }, []);

    return (
        <div ref={rootRef} className="min-h-screen bg-white font-inter selection:bg-[#FF8B11] selection:text-white">
            {/* Progress Bar (desktop: fixed above everything) */}
            <motion.div
                className="hidden md:block fixed top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#FF8B11] to-[#FFD700] origin-left z-[100]"
                style={{ scaleX }}
            />

            {/* Mobile: progress → banner → header travel together as one sticky
                group. Desktop: a plain wrapper — the header stays absolute. */}
            <div ref={stickyRef} className="sticky top-0 z-40 md:static md:z-auto">
                <motion.div
                    className="md:hidden h-1 w-full origin-left bg-[linear-gradient(90deg,#FF8B11,#FFD700)]"
                    style={{ scaleX }}
                />

                {/* Announcement Banner */}
                {variant === 'queued' ? (
                    <div className="bg-[#5A2A0D] text-white py-2.5 md:py-3.5 px-4 text-center text-[12px] leading-[1.45] md:text-sm md:leading-5 md:tracking-wide z-40 relative top-0 w-full flex flex-col items-center justify-center gap-0.5">
                        <span className="font-medium text-white/90">
                            <span className="font-bold text-[#FFD700]">You're on the list!</span> Thanks for being patient.
                        </span>
                        <span className="text-white/90">We'll email you the moment a spot opens up.</span>
                    </div>
                ) : (
                    <div className="bg-[#5A2A0D] text-white py-2.5 md:py-3.5 px-4 text-center text-[12px] leading-[1.45] md:text-sm md:leading-5 md:tracking-wide z-40 relative top-0 w-full flex flex-col items-center justify-center gap-0.5">
                        <span className="font-medium text-white/90">
                          We're full at the moment -{" "}
                          <button
                            onClick={() => setShowWaitlist(true)}
                            className="font-bold underline underline-offset-[3px] md:underline-offset-4 decoration-[#FFD700]/50 hover:decoration-[#FFD700] hover:text-[#FFD700] transition-colors"
                          >
                            Tap here to join the waitlist
                          </button>.
                        </span>
                        <span className="text-white/90">Get <span className="font-bold text-[#FFD700]">$20 OFF</span> as a thank-you for your patience</span>
                    </div>
                )}

                <Header logoVariant="white" onGetStartedClick={() => setShowWaitlist(true)} queued={variant === 'queued'} mobileRedesign />
            </div>

            <main>
                {/* Crawlable brand summary for search & answer engines. Visually hidden
                    (screen-reader accessible) so it doesn't alter the hero design, but it
                    gives crawlers real, brand-first content about what Cello is. */}
                <section className="sr-only" aria-label="About Cello">
                    <h1>Cello — hyper-personalized personal styling from expert stylists, powered by AI</h1>
                    <p>
                        Cello is a personal styling service that pairs you with a real expert stylist,
                        powered by AI, to create outfits from the wardrobe you already own. Cello digitizes
                        your closet in under 30 minutes, uncovers the gaps, and delivers hyper-personalized
                        outfit recommendations, bespoke shopping guides, and year-round on-demand styling —
                        so getting dressed is effortless and you always look put together. Whether you want a
                        single seasonal wardrobe edit or full-year styling, Cello's stylists build a practical,
                        modern wardrobe around your life, your body, and your budget.
                    </p>
                </section>

                <HeroBass onGetStartedClick={() => setShowWaitlist(true)} queued={variant === 'queued'} mobileRedesign />
                <WhyChooseCelloBass onGetStartedClick={() => setShowWaitlist(true)} queued={variant === 'queued'} images={variant !== 'queued' ? MENS_PHOTOS : undefined} mobileRedesign />
                <AwardsBass headline={variant !== 'queued' ? "Cello is the nation's largest men's styling service in the US" : undefined} mobileRedesign />
                <HowItWorksBass mobileRedesign />
                {/* Portfolio: /mens-styling filters to his; splash (default) shows all
                    looks sorted with his first so the initial viewport is men-only and
                    horizontal scroll reveals women's looks. /queued gets the plain mix. */}
                <JoySectionBass
                    closetFilter={variant === 'mens' ? 'his' : undefined}
                    order={variant === 'default' ? 'mens-first' : 'default'}
                    mobileRedesign
                />
                <TestimonialsBass mobileRedesign />
                <LifestyleBandBass onGetStartedClick={() => setShowWaitlist(true)} queued={variant === 'queued'} mobileRedesign />
                <PricingEdits onJoinClick={() => setShowWaitlist(true)} startExpanded={false} />
                <FAQSectionBass mobileRedesign />
                <GapFinderQuiz />
                <CTABass onGetStartedClick={() => setShowWaitlist(true)} queued={variant === 'queued'} mobileRedesign />
            </main>

            <Footer mobileRedesign />
            {/* $20 here is home-page only — other landing pages keep the $30 default. */}
            <WaitlistDialog open={showWaitlist} onOpenChange={setShowWaitlist} discountLabel="$20 OFF" />
        </div>
    );
};

export default Landing8;
